import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Fragment, useState } from 'react';

import type { StorySlide } from '@sorare/core/src/components/Story';
import { Story } from '@sorare/core/src/components/Story';
import { usePausableStory } from '@sorare/core/src/components/Story/usePausableStory';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { isType } from '@sorare/core/src/lib/gql';

import { RewardUnlockedStory } from 'components/missions/stories/RewardUnlockedStory';
import { useClaimTask } from 'components/missions/useClaimTask';

import { ChooseYourRewardStory } from '../DecisivePlayerPickerTaskStatus/ChooseYourRewardStory';
import { ClaimYourRewardsStory } from '../DecisivePlayerPickerTaskStatus/ClaimYourRewardsStory';
import type { DecisivePlayerPickerTaskClaimStory_decisivePlayerPickerTask } from './__generated__/index.graphql';

type Props = {
  task: DecisivePlayerPickerTaskClaimStory_decisivePlayerPickerTask;
  onClose: () => void;
};
export const DecisivePlayerPickerTaskClaimStory = ({
  task,
  onClose,
}: Props) => {
  const { pause, handleOnPause, handleOnUnpause } = usePausableStory();
  const [claimTask] = useClaimTask();
  const { sport } = useSportContext();
  const [signedAlternativeRewardConfigs, setSignedAlternativeRewardConfigs] =
    useState<string | undefined>();

  const { isMobileApp, postMessage } = useIsMobileApp();

  const isDecisivePlayerPickerTask = isType(task, 'DecisivePlayerPickerTask');
  if (!isDecisivePlayerPickerTask) {
    return null;
  }

  const rewardConfigs = task.rewardConfigs || [];
  const alternativeRewardConfigs = task.alternativeRewardConfigs || [];
  const canChooseAlternativeRewardConfigs = alternativeRewardConfigs.length > 0;

  const normalizedTaskAppearances = Array.from(
    {
      length: task.maxAppearancesCount,
    },
    (_, index) => {
      return task.taskAppearances[index];
    }
  );

  return (
    <Story
      pause={pause}
      handleOnPause={handleOnPause}
      handleOnUnpause={handleOnUnpause}
      goBackToBeginning={false}
      onClose={() => {
        if (isMobileApp) {
          postMessage('taskClaimed', { taskId: task.id });
        }
        onClose();
      }}
      stories={[
        {
          duration: 2,
          withBackChevron: false,
          withNextChevron: false,
          autoPlayNext: false,
          bodyElement: ({ onNext }) => (
            <Fragment key="decisive-player-picker-story">
              <ClaimYourRewardsStory
                taskAppearances={normalizedTaskAppearances}
                mode={task.mode}
                onContinue={async () => {
                  if (!canChooseAlternativeRewardConfigs) {
                    await claimTask({
                      variables: {
                        taskId: task.id,
                        sport,
                      },
                    });
                  }
                  onNext();
                }}
              />
            </Fragment>
          ),
        },
        ...(canChooseAlternativeRewardConfigs
          ? [
              {
                duration: 2,
                withBackChevron: false,
                withNextChevron: false,
                autoPlayNext: false,
                bodyElement: ({ onNext }) => (
                  <Fragment key="choose-reward-story">
                    <ChooseYourRewardStory
                      rewardConfigs={rewardConfigs}
                      alternativeRewardConfigs={alternativeRewardConfigs}
                      setSignedAlternativeRewardConfigs={
                        setSignedAlternativeRewardConfigs
                      }
                      onConfirm={async () => {
                        await claimTask({
                          variables: {
                            taskId: task.id,
                            signedAlternativeRewardConfigs,
                            sport,
                          },
                        });
                        onNext();
                      }}
                    />
                  </Fragment>
                ),
              } as StorySlide,
            ]
          : []),
        {
          duration: 2,
          withBackChevron: false,
          withNextChevron: false,
          autoPlayNext: false,
          bodyElement: ({ onNext }) => (
            <Fragment key="reward-unlocked-story">
              <RewardUnlockedStory
                open
                onClose={onNext}
                task={task}
                alternativeRewardConfigs={
                  signedAlternativeRewardConfigs
                    ? alternativeRewardConfigs[0].rewardConfigs
                    : undefined
                }
              />
            </Fragment>
          ),
        },
      ]}
    />
  );
};

DecisivePlayerPickerTaskClaimStory.fragments = {
  taskInterface: gql`
    fragment DecisivePlayerPickerTaskClaimStory_decisivePlayerPickerTask on DecisivePlayerPickerTask {
      id
      mode
      rewardConfigs {
        id
        ...ChooseYourRewardStory_rewardConfig
      }
      alternativeRewardConfigs {
        ...ChooseYourRewardStory_alternativeRewardConfig
      }
      ...ClaimYourRewardsStory_taskInterface
      ...RewardUnlockedStory_taskInterface
    }
    ${ClaimYourRewardsStory.fragments.taskInterface}
    ${ChooseYourRewardStory.fragments.rewardConfig}
    ${ChooseYourRewardStory.fragments.alternativeRewardConfig}
    ${RewardUnlockedStory.fragments.taskInterface}
  ` as TypedDocumentNode<DecisivePlayerPickerTaskClaimStory_decisivePlayerPickerTask>,
};
